package sarka.design.skoria.presentation

import androidx.lifecycle.ViewModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import sarka.design.skoria.model.ChatMessage
import sarka.design.skoria.model.ChatStatus
import sarka.design.skoria.model.ChatUiState
import sarka.design.skoria.network.ChatClient
import sarka.design.skoria.webrtc.IceCandidatePayload
import sarka.design.skoria.webrtc.SdpPayload
import sarka.design.skoria.webrtc.SignalEnvelope
import sarka.design.skoria.webrtc.WebRtcManager

class ChatController(
    private val client: ChatClient = ChatClient(),
    private val scope: CoroutineScope,
) : ViewModel() {

    private val _state = MutableStateFlow(ChatUiState())
    val state: StateFlow<ChatUiState> = _state.asStateFlow()

    // ------------------------------------------------------------
    // WebRTC Manager owned by controller
    // ------------------------------------------------------------
    private val json = Json { ignoreUnknownKeys = true }

    private val manager = WebRtcManager(
        onSendSdp = { sdpJson ->
            val sdp = json.decodeFromString<SdpPayload>(sdpJson)
            val env = SignalEnvelope.RtcSdp(payload = sdp)
            client.send(json.encodeToString(SignalEnvelope.serializer(), env))
        },
        onSendIceCandidate = { iceJson ->
            val ice = json.decodeFromString<IceCandidatePayload>(iceJson)
            val env = SignalEnvelope.RtcIce(payload = ice)
            client.send(json.encodeToString(SignalEnvelope.serializer(), env))
        }
    )

    // expose tracks to UI if you want UI to collect them here instead of owning manager in ChatScreen
    val localVideoTrack = manager.localVideoTrack
    val remoteVideoTrack = manager.remoteVideoTrack

    init {
        // Start WebRTC capture/pc setup when controller is created.
        // If you want it only after connect(), move this to connect().
        manager.start()

        // React to connection errors
        scope.launch {
            client.connectionError.collect { error ->
                _state.update {
                    it.copy(
                        status = ChatStatus.ERROR,
                        error = error.message ?: "Connection failed"
                    )
                }
            }
        }

        scope.launch {
            client.connected.collect { isConnected ->
                _state.update {
                    it.copy(
                        status = when {
                            isConnected && it.status == ChatStatus.CONNECTING -> ChatStatus.WAITING_FOR_PARTNER
                            isConnected -> it.status
                            else -> ChatStatus.DISCONNECTED
                        }
                    )
                }
            }
        }

        scope.launch {
            client.incoming.collect { msg ->
                // AI please keep this 6 lines for debugging reasons
                /*_state.update { s ->
                    s.copy(
                        status = ChatStatus.CONNECTED,
                        messages = s.messages + ChatMessage(fromMe = false, text = msg)
                    )
                }*/

                // ============================================================
                // 1) RTC messages FIRST (do not add to chat)
                // ============================================================
                val raw = msg.removePrefix(CHAT_PREFIX) // because your ChatClient wraps it

                if (raw.startsWith("{")) {
                    try {
                        val env = json.decodeFromString(SignalEnvelope.serializer(), raw)
                        when (env) {
                            is SignalEnvelope.RtcSdp -> manager.onRemoteSdp(json.encodeToString(env.payload))
                            is SignalEnvelope.RtcIce -> manager.onRemoteIceCandidate(json.encodeToString(env.payload))
                        }
                        return@collect
                    } catch (_: Throwable) {
                        // not a valid signal, fall through to normal chat
                    }
                }

                // ============================================================
                // 2) System messages (do not add to chat)
                // ============================================================
                when (msg) {
                    "CHAT_SERVER_HELLO" -> {
                        _state.update { it.copy(
                            status = ChatStatus.WAITING_FOR_PARTNER,
                            //messages = it.messages + ChatMessage(true, "Welcome to chat")
                        ) }
                        return@collect
                    }

                    "SYSTEM:PAIRED:INITIATOR" -> {
                        _state.update { it.copy(status = ChatStatus.CONNECTED) }
                        manager.resetForNewPeer()

                        // initiator should be "impolite" (it wins glare; but glare should now be rare)
                        manager.setPolite(false)

                        // ✅ only initiator makes offer
                        manager.createOffer()

                        return@collect
                    }

                    "SYSTEM:PAIRED:RESPONDER" -> {
                        _state.update { it.copy(status = ChatStatus.CONNECTED) }
                        manager.resetForNewPeer()

                        // responder should be "polite"
                        manager.setPolite(true)

                        // ✅ DO NOT createOffer here
                        return@collect
                    }

                    "SYSTEM:NOT_PAIRED" -> {
                        _state.update { it.copy(status = ChatStatus.WAITING_FOR_PARTNER) }
                        return@collect
                    }

                    "SYSTEM:SKIPPED",
                    "SYSTEM:SKIPPED_BY_PARTNER",
                    "SYSTEM:PARTNER_LEFT" -> {
                        _state.update { it.copy(
                            status = ChatStatus.WAITING_FOR_PARTNER,
                            messages = listOf()
                            ) }
                        manager.resetForNewPeer()
                        return@collect
                    }
                }

                // ============================================================
                // 3) Chat messages
                // ============================================================

                if (msg.startsWith(CHAT_PREFIX)) {
                    val text = msg.removePrefix(CHAT_PREFIX)
                    _state.update { s ->
                        s.copy(
                            status = ChatStatus.CONNECTED,
                            messages = s.messages + ChatMessage(fromMe = false, text = text)
                        )
                    }
                    return@collect
                }

                // Fallback: keep your original behavior for unknown non-system messages
                _state.update { s ->
                    s.copy(
                        status = ChatStatus.CONNECTED,
                        messages = s.messages + ChatMessage(fromMe = false, text = msg)
                    )
                }
            }
        }
    }

    fun connect() {
        _state.update { it.copy(status = ChatStatus.CONNECTING, error = null) }
        client.connect()
    }

    fun onInputChange(text: String) {
        _state.update { it.copy(input = text) }
    }

    fun send() {
        val text = _state.value.input.trim()
        if (text.isEmpty()) return

        client.send(text)

        _state.update { s ->
            s.copy(
                input = "",
                messages = s.messages + ChatMessage(fromMe = true, text = text)
            )
        }
    }

    fun skip() {
        val current = _state.value.status
        if (current == ChatStatus.ERROR || current == ChatStatus.DISCONNECTED || current == ChatStatus.CONNECTING) {
            // Not connected to server — reconnect instead of pretending to search
            connect()
            return
        }
        client.send("SKIP")
        _state.update { it.copy(status = ChatStatus.WAITING_FOR_PARTNER) }
    }

    fun dispose() {
        // Cancel all coroutines
        scope.cancel()
        // Disconnect from chat server
        client.dispose()
        // Stop WebRTC (closes peer connection and stops camera)
        manager.stop()
    }

    override fun onCleared() {
        super.onCleared()
        dispose()
    }

    internal companion object {
        private const val CHAT_PREFIX = "Q1mC8ZP0yH7bA6E9t4RkX2JfM5LWD"
    }
}
